import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { Badge } from '../components/ui/badge';
import {
  Search, Users, TrendingUp, MapPin, Heart, X, Check, Loader2,
  ArrowUpDown, UserPlus, Plus, Save, Trash2, ChevronDown, ChevronRight,
  Shield, ShieldCheck, Star, Zap, Clock, DollarSign, Eye, Package, Activity, FileText,
  Download, ArrowRight, Sparkles, Filter, BarChart3, ShoppingCart, Wand2,
} from 'lucide-react';
import { useWaveCart } from '../contexts/WaveCartContext';
import { useAuth } from '../contexts/AuthContext';
import { discoverCreators, GENERATIONS } from '../services/dsp';
import CreatorProfileModal from '../components/creator/CreatorProfileModal';
import MatchExplainerModal from '../components/dna/MatchExplainerModal';
import { FilterSection } from '../components/discover/FilterSection';
import CreatorCard from '../components/discover/CreatorCard';
import CmdCohortMap from '../components/discover/CmdCohortMap';
import { useCmdDiscoverSignals } from '../hooks/useCmdDiscoverSignals';
import {
  FILTER_CATEGORIES, QUICK_PILLS, RECOMMENDED_COHORTS,
  T, P, BG, CARD, BORDER,
} from '../components/discover/discoverConstants';
import { useDemoProfile } from '../hooks/useDemoProfile';

const API = process.env.REACT_APP_BACKEND_URL;


// ═══════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════
export default function DiscoverCreatorsPage() {
  const { user } = useAuth();
  const { profile, isDemo } = useDemoProfile();
  const brandScale = user?.brand_scale || 'dtc_national';
  const isLocalBrand = brandScale === 'single_location' || brandScale === 'multi_location';

  // Demo brands replace the generic quick pills + cohort recs with brand-
  // specific ones (e.g. "Off-Road Overlanding · 25K-100K" for Helix Motors).
  const quickPills = (isDemo && profile?.discover_quick_pills?.length)
    ? profile.discover_quick_pills
    : QUICK_PILLS;
  const recommendedCohorts = (isDemo && profile?.discover_recommended_cohorts?.length)
    ? profile.discover_recommended_cohorts
    : RECOMMENDED_COHORTS;

  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Default to engagement-rate so the micro-creators surfaced by Wave's
  // orchestration thesis appear first (small accounts have higher engagement).
  // Sorting by raw follower count would put celebrities at the top and
  // contradict the platform's value prop.
  const [sortBy, setSortBy] = useState('engagement');
  const [filters, setFilters] = useState({});
  const [expandedSections, setExpandedSections] = useState(new Set(['demographics', 'platform', 'niche']));
  // Layer 3 — Opportunity Radar deep-link banner. When the user lands
  // from a leaderboard CTA, location.state.trendingPreset carries the
  // trend's niche + top competitor name so we can show "Showing creators
  // primed for #GlassSkinProtocol · Outpace Petal & Fern".
  const [trendingPresetBanner, setTrendingPresetBanner] = useState(null);
  const location = useLocation();
  const { selectedCreators: cartCreators, addCreator, removeCreator, isInCart, count: cartCount } = useWaveCart();

  // Apply trending preset (Layer 3 deep-link) once on mount when the
  // user arrives from the Opportunity Radar leaderboard "Find them" CTA.
  useEffect(() => {
    const preset = location.state?.trendingPreset;
    if (!preset?.niche) return;
    // Niche format from backend: "Beauty / Skincare" or "Sound / Audio".
    // Take the first segment, capitalize, and pin it as primary_niche.
    const firstSegment = String(preset.niche).split('/')[0].trim();
    if (firstSegment) {
      setFilters(prev => ({
        ...prev,
        primary_niche: [firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1)],
      }));
      setShowBuilder(true);
    }
    setTrendingPresetBanner({
      niche:     preset.niche,
      topBrand:  preset.top_brand,
      trendTag:  preset.trend_tag,
    });
    // Wipe router state so a refresh/back doesn't re-fire the preset.
    window.history.replaceState({}, '', location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DSP AI-match state
  const [aiBrief, setAiBrief] = useState('');
  const [aiGeneration, setAiGeneration] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiError, setAiError] = useState(null);
  const [aiSource, setAiSource] = useState(null);  // 'dna' (semantic v2) | 'keyword' (legacy fallback)
  const [profileHandle, setProfileHandle] = useState(null);
  const [explainCreator, setExplainCreator] = useState(null);  // creator obj currently in MatchExplainer

  const runAiMatch = async () => {
    if (!aiBrief.trim() || aiLoading) return;
    setAiLoading(true); setAiError(null);
    try {
      const payload = { campaign_brief: aiBrief, top_n: 12 };
      if (aiGeneration) payload.generation = aiGeneration;
      const res = await discoverCreators(payload);
      setAiResults(res.creators || []);
      setAiSource(res.source || null);
    } catch (err) {
      const d = err.response?.data?.detail;
      setAiError(typeof d === 'string' ? d : err.message || 'AI match failed');
    } finally {
      setAiLoading(false);
    }
  };

  const selectedCreators = useMemo(() => new Set(cartCreators.map(c => c.id)), [cartCreators]);

  // One-click action for Wave Composition's "Add all to Wave" CTA.
  // Pushes the suggested creators into the cart context; the existing
  // "Push to Build Wave" link in the results header picks them up next.
  const addAllToWave = useCallback((picks) => {
    (picks || []).forEach((pick) => {
      if (!isInCart(pick.creator.id)) addCreator(pick.creator);
    });
  }, [addCreator, isInCart]);

  const toggleSection = (id) => setExpandedSections(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCreator = (id) => {
    if (isInCart(id)) {
      removeCreator(id);
    } else {
      const creator = creators.find(c => c.id === id);
      if (creator) addCreator(creator);
    }
  };

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0) && v !== null).length;
  }, [filters]);

  // Build boolean preview from active filters
  const booleanPreview = useMemo(() => {
    const parts = [];
    Object.entries(filters).forEach(([key, val]) => {
      if (!val || (Array.isArray(val) && val.length === 0)) return;
      const field = FILTER_CATEGORIES.flatMap(c => c.fields).find(f => f.key === key);
      if (!field) return;
      if (Array.isArray(val)) parts.push(`${field.label} = (${val.join(' OR ')})`);
      else if (typeof val === 'object' && val.min) parts.push(`${field.label} ${val.min}${val.max ? `–${val.max}` : '+'}`);
      else if (typeof val === 'number') parts.push(`${field.label} >= ${val}${field?.unit || ''}`);
      else parts.push(`${field.label} = "${val}"`);
    });
    return parts.length > 0 ? parts.join(' AND ') : 'No filters active';
  }, [filters]);

  // Fetch creators from API
  const fetchCreators = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const genderArr = filters.gender || [];
      if (genderArr.length === 1 && genderArr[0] !== 'All Genders') params.set('gender', genderArr[0].toLowerCase().replace('-', '_'));
      const platformArr = filters.platform || [];
      if (platformArr.length === 1) params.set('platform', platformArr[0].toLowerCase());
      const nicheArr = filters.primary_niche || [];
      if (nicheArr.length === 1) params.set('category', nicheArr[0].toLowerCase());
      if (filters.location) params.set('location', filters.location);
      if (filters.min_engagement) params.set('min_rating', String(filters.min_engagement / 3));
      const ethArr = filters.ethnicity || [];
      if (ethArr.includes('HBCU')) params.set('is_hbcu', 'true');
      // iter 57: "Near my store" filter. Backend derives the brand's top-market
      // state abbr from the uploaded footprint and regex-matches creator.location.
      if (filters.near_me) params.set('near_me', 'true');
      const { data } = await axios.get(`${API}/api/brand/creators?${params.toString()}`, { withCredentials: true });
      setCreators(data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Fetch creators failed:', err);
    } finally { setLoading(false); }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCreators(); }, [fetchCreators]);

  const displayed = useMemo(() => {
    let result = [...creators];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c => c.name?.toLowerCase().includes(q) || c.handle?.toLowerCase().includes(q) || c.bio?.toLowerCase().includes(q));
    }
    if (sortBy === 'followers') result.sort((a, b) => (b.followers || 0) - (a.followers || 0));
    else if (sortBy === 'engagement') result.sort((a, b) => (b.engagement_rate || 0) - (a.engagement_rate || 0));
    else if (sortBy === 'rating') result.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
    return result;
  }, [creators, searchQuery, sortBy]);

  // CMD safe-surface signals for the currently-displayed creator set.
  // Per /app/docs/CMD_PIPELINE_HEALTH_AUDIT.md §4 + §9:
  //   • Backend-gated by CMD_DISCOVER_ENABLED (default off in prod).
  //   • Returns only patch/role/coupling/latent/similar-pair data — no
  //     quantified lift or revenue claims.
  //   • UI guard (signals.uiGuard.hide_scatter) hides the scatter when
  //     the projection is degenerate.
  // Cap at 60 so the O(N²) pairwise overlap stays sub-second on large queries.
  const displayedIds = useMemo(
    () => displayed.slice(0, 60).map(c => c.id).filter(Boolean),
    [displayed],
  );
  const cmdSignals = useCmdDiscoverSignals(displayedIds);

  const applyQuickPill = (pill) => {
    const newFilters = {};
    if (pill.filters.category) newFilters.primary_niche = [pill.filters.category.charAt(0).toUpperCase() + pill.filters.category.slice(1)];
    if (pill.filters.gender) newFilters.gender = [pill.filters.gender.charAt(0).toUpperCase() + pill.filters.gender.slice(1)];
    if (pill.filters.platform) newFilters.platform = [pill.filters.platform.charAt(0).toUpperCase() + pill.filters.platform.slice(1)];
    if (pill.filters.location) newFilters.location = pill.filters.location;
    if (pill.filters.is_hbcu) newFilters.ethnicity = ['HBCU'];
    setFilters(newFilters);
    setShowBuilder(true);
  };

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-[1600px] mx-auto relative" style={{ background: BG }} data-testid="discover-creators-page">
        <span className="absolute top-3 right-5 text-[7px] text-gray-700 uppercase tracking-widest">Patent Pending</span>

        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <h1 className="text-xl font-black text-white flex items-center gap-2"><Users className="h-5 w-5" style={{ color: T }} />Discover Creators</h1>
            {/* iter 59: platform-level brand-safety guarantee — said ONCE, not sprinkled per creator */}
            <Link
              to="/brand-safety"
              data-testid="platform-brand-safety-badge"
              className="mt-1 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] font-bold text-gray-500 hover:text-[#4ade80] transition-colors"
            >
              <ShieldCheck className="h-3 w-3 text-[#4ade80]" />
              Every creator brand-safe · Verified by WAVE
            </Link>
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, handle, or bio..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="creator-search" />
          </div>
          <button onClick={() => setShowBuilder(!showBuilder)}
            className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all"
            style={{ background: showBuilder ? `${T}15` : CARD, color: showBuilder ? T : '#888', border: `1px solid ${showBuilder ? `${T}40` : BORDER}` }}
            data-testid="toggle-boolean-builder">
            <Filter className="h-3.5 w-3.5" />{showBuilder ? 'Close Builder' : 'Open Advanced Filter Builder'}
            {activeFilterCount > 0 && <span className="w-4 h-4 rounded-full text-[8px] font-bold flex items-center justify-center" style={{ background: T, color: '#000' }}>{activeFilterCount}</span>}
          </button>
        </div>

        {/* Layer 3 — Opportunity Radar deep-link banner. Visible only
            when the user arrived from a trending leaderboard "Find them"
            CTA. Frames the next action ("you came here to outpace
            X") and is dismissible. */}
        {trendingPresetBanner && (
          <div
            className="rounded-xl px-4 py-3 mb-4 flex items-start gap-3"
            style={{
              background: 'linear-gradient(90deg, rgba(0,212,255,0.12), rgba(255,77,148,0.05))',
              border: '1px solid rgba(0,212,255,0.32)',
            }}
            data-testid="trending-preset-banner"
          >
            <Zap className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#00D4FF' }} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-black mb-0.5" style={{ color: '#00D4FF' }}>
                From Opportunity Radar
              </p>
              <p className="text-[12px] text-white leading-snug">
                Creators primed for{' '}
                <strong className="font-bold text-[#00D4FF]">{trendingPresetBanner.trendTag}</strong>
                {trendingPresetBanner.topBrand && (
                  <>
                    {' '}— outpace{' '}
                    <strong className="font-bold text-[#FF4D94]">{trendingPresetBanner.topBrand}</strong>
                  </>
                )}
                <span className="text-gray-500 ml-2">·</span>{' '}
                <span className="text-gray-400">filtered to{' '}
                  <strong className="text-white">{trendingPresetBanner.niche}</strong>
                </span>
              </p>
            </div>
            <button
              onClick={() => {
                setTrendingPresetBanner(null);
                setFilters({});
              }}
              data-testid="dismiss-trending-banner"
              className="text-[9px] uppercase tracking-wider font-bold text-gray-500 hover:text-white px-2 py-1 rounded transition-colors"
            >
              Clear
            </button>
          </div>
        )}

        {/* Quick-filter pills */}
        <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="quick-pills">
          {isLocalBrand && (
            <button
              onClick={() => setFilters(f => ({ ...f, near_me: !f.near_me }))}
              data-testid="near-me-toggle"
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider transition-all"
              style={{
                background: filters.near_me ? '#FF4D94' : CARD,
                color: filters.near_me ? '#fff' : '#888',
                border: `1px solid ${filters.near_me ? '#FF4D94' : BORDER}`,
                boxShadow: filters.near_me ? '0 0 14px rgba(255,77,148,0.3)' : 'none',
              }}
            >
              <MapPin className="h-3 w-3" />
              Near my store
            </button>
          )}
          {quickPills.map(pill => (
            <button key={pill.label} onClick={() => applyQuickPill(pill)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[9px] font-semibold transition-all" style={{ background: CARD, color: '#888', border: `1px solid ${BORDER}` }}
              data-testid={`pill-${pill.label.replace(/\s/g, '-').toLowerCase()}`}>{pill.label}</button>
          ))}
        </div>

        {/* ───  Micro-creator thesis callout — visible to every brand ───
            WAVE's product thesis: 80% of all creators are micro (1K-100K)
            and orchestrating them is what produces a compounding Wave Pulse.
            Surface this at the top of Discover so investors and onboarding
            users see it before scrolling the grid. */}
        <div className="rounded-xl p-4 mb-4 flex items-start gap-3"
             style={{ background: 'linear-gradient(90deg, rgba(0,212,255,0.07), rgba(255,77,148,0.04))', border: '1px solid rgba(0,212,255,0.2)' }}
             data-testid="micro-creator-thesis">
          <Sparkles className="h-4 w-4 text-[#00D4FF] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-black text-white tracking-wide uppercase mb-0.5">
              Wave Orchestration · The Micro-Creator Compounding Effect
            </p>
            <p className="text-[11px] text-gray-300 leading-relaxed">
              <span className="text-[#00D4FF] font-bold">80%</span> of all eligible creators sit in the <span className="text-white font-bold">1K–100K</span> band.
              Wave's algorithm fires waves of <span className="text-white font-bold">30–180 micro-creators</span> across niches in the same window —
              audiences overlap, engagement compounds, and your <span className="text-white font-bold">Wave Pulse</span> climbs higher than any single celebrity placement can drive.
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          {/* ═══ ADVANCED FILTER BUILDER ═══ */}
          {showBuilder && (
            <div className="w-[320px] flex-shrink-0 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto no-scrollbar" data-testid="filter-builder">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-bold text-white">Build Your Perfect Cohort</h3>
                <button onClick={() => setFilters({})} className="text-[8px] text-gray-600 hover:text-white transition-all flex items-center gap-1" data-testid="clear-all-filters">
                  <Trash2 className="h-2.5 w-2.5" />Clear All
                </button>
              </div>

              {/* 12 Category Sections */}
              {FILTER_CATEGORIES.map(cat => (
                <FilterSection key={cat.id} cat={cat} filters={filters} setFilters={setFilters}
                  expanded={expandedSections.has(cat.id)} onToggle={() => toggleSection(cat.id)} />
              ))}

              {/* Boolean Preview */}
              <div className="rounded-lg p-3 space-y-2" style={{ background: CARD, border: `1px solid ${T}20` }}>
                <p className="text-[8px] uppercase font-semibold" style={{ color: T }}>Boolean Query Preview</p>
                <p className="text-[9px] font-mono leading-relaxed break-words" style={{ color: T }} data-testid="boolean-preview">{booleanPreview}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button className="flex-1 px-3 py-2 rounded-lg text-[9px] font-bold flex items-center justify-center gap-1" style={{ background: `${T}12`, color: T, border: `1px solid ${T}30` }} data-testid="save-filter">
                  <Save className="h-3 w-3" />Save Filter
                </button>
                <button className="flex-1 px-3 py-2 rounded-lg text-[9px] font-bold flex items-center justify-center gap-1" style={{ background: BG, color: '#888', border: `1px solid ${BORDER}` }} data-testid="export-csv">
                  <Download className="h-3 w-3" />Export CSV
                </button>
              </div>

              {/* Saved Filters */}
              <div className="rounded-lg p-3 space-y-2" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                <p className="text-[8px] text-gray-500 uppercase font-semibold">Saved Filters</p>
                {['Health & Wellness Moms', 'Sleep Consultants – High ShopMy', 'Skincare Micro – West Coast'].map(name => (
                  <button key={name} className="w-full text-left px-2 py-1.5 rounded text-[9px] text-gray-400 hover:text-white transition-all" style={{ background: BG, border: `1px solid ${BORDER}` }}>{name}</button>
                ))}
              </div>
            </div>
          )}

          {/* ═══ MAIN CONTENT ═══ */}
          <div className="flex-1 min-w-0">
            {/* ═══ AI-POWERED MATCH BY BRIEF (DSP endpoint) ═══ */}
            {!showBuilder && (
              <div className="mb-4 rounded-xl p-4" style={{ background: CARD, border: `1px solid ${T}25` }} data-testid="ai-match-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="h-4 w-4" style={{ color: T }} />
                  <h3 className="text-xs font-black text-white">AI Match by Campaign Brief</h3>
                  <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${T}15`, color: T, border: `1px solid ${T}30` }}>Digital DNA™</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <textarea value={aiBrief} onChange={e => setAiBrief(e.target.value)} rows={2}
                    placeholder="Describe your ideal creator in plain English — e.g., 'Gen Z skincare creators for a summer SPF drop on TikTok.'"
                    className="flex-1 min-w-[280px] px-3 py-2 rounded-lg text-xs text-white placeholder-gray-600 resize-none focus:outline-none"
                    style={{ background: BG, border: `1px solid ${BORDER}` }}
                    data-testid="ai-brief-input" />
                  <div className="flex flex-col gap-2">
                    <select value={aiGeneration} onChange={e => setAiGeneration(e.target.value)}
                      className="px-2.5 py-2 rounded-lg text-[11px] text-white focus:outline-none"
                      style={{ background: BG, border: `1px solid ${BORDER}` }}
                      data-testid="ai-generation-select">
                      <option value="">Any Generation</option>
                      {GENERATIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                    <button onClick={runAiMatch} disabled={!aiBrief.trim() || aiLoading}
                      className="px-4 py-2 rounded-lg text-[11px] font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-40"
                      style={{ background: '#FF4D94', boxShadow: '0 0 16px rgba(255,77,148,0.35)' }}
                      data-testid="ai-match-btn">
                      {aiLoading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Matching…</> : <><Sparkles className="h-3.5 w-3.5" />Find my creators</>}
                    </button>
                  </div>
                </div>
                {aiError && <p className="text-[10px] text-red-400 mt-2" data-testid="ai-match-error">{aiError}</p>}
                {aiResults.length > 0 && (
                  <div className="mt-3" data-testid="ai-match-results">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[9px] text-gray-500 uppercase font-semibold tracking-wider">
                        {aiResults.length} Top Matches by Digital DNA
                      </p>
                      {aiSource === 'dna' && (
                        <span
                          data-testid="dna-v2-badge"
                          title="Powered by Digital DNA — hybrid semantic matching (rule-based 0.55 + audience embedding cosine 0.45) with intra-wave overlap penalty so the cohort spans the widest unique audience surface."
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-black tracking-wider uppercase"
                          style={{
                            background: `linear-gradient(135deg, ${T}22, ${P}22)`,
                            border: `1px solid ${T}55`,
                            color: T,
                          }}
                        >
                          <Sparkles className="h-2.5 w-2.5" />
                          Digital&nbsp;DNA&nbsp;·&nbsp;Hybrid
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {aiResults.map(c => (
                        <button key={c.handle}
                          onClick={() => {
                            // DNA v2 results have a creator_email + (often) explanation.
                            // Open the MatchExplainer so brands see the *why*. For keyword
                            // results, fall through to the regular creator profile.
                            if (aiSource === 'dna' && c.creator_email) {
                              setExplainCreator(c);
                            } else {
                              setProfileHandle(c.handle);
                            }
                          }}
                          className="p-2.5 rounded-lg text-left transition-all hover:border-[#00F5FF]/50"
                          style={{ background: BG, border: `1px solid ${BORDER}` }}
                          data-testid={`ai-result-${c.handle.replace('@', '')}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-bold text-white truncate">{c.name}</span>
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black flex-shrink-0 ml-1" style={{ color: T }}>
                              {aiSource === 'dna' && <Sparkles className="h-2 w-2" />}
                              {c.match_score?.toFixed?.(1) ?? c.match_score}%
                            </span>
                          </div>
                          <p className="text-[9px]" style={{ color: T }}>{c.handle}</p>
                          <p className="text-[9px] text-gray-500 mt-1">
                            {(c.followers || 0).toLocaleString()} • {c.engagement_rate?.toFixed?.(1) ?? c.engagement_rate}% eng
                          </p>
                          {c.generation_label && (
                            <span className="inline-block mt-1 text-[8px] px-1.5 py-0.5 rounded font-semibold"
                              style={{ background: `${P}15`, color: P, border: `1px solid ${P}30` }}>{c.generation_label}</span>
                          )}
                          {aiSource === 'dna' && (
                            <p className="text-[8px] text-gray-600 mt-1.5 italic">
                              Click to see DNA breakdown
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recommended Cohorts */}
            {!showBuilder && (
              <div className="mb-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Recommended Cohorts for {(user?.brand_name || 'You')}</h3>
                <div className="grid grid-cols-3 gap-3" data-testid="recommended-cohorts">
                  {recommendedCohorts.map(c => (
                    <div key={c.name} className="p-3 rounded-xl transition-all cursor-pointer group" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                      <h4 className="text-[11px] font-bold text-white mb-1 group-hover:text-[#00F5FF] transition-colors">{c.name}</h4>
                      <p className="text-[9px] text-gray-500 mb-2">{c.desc}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-bold" style={{ color: T }}>DNA Match: {c.match}</span>
                        <span className="text-[9px] text-gray-600">{c.creators} creators</span>
                        <ArrowRight className="h-3 w-3 text-gray-600 ml-auto group-hover:text-[#00F5FF] transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-white">{displayed.length} Creators</span>
                {loading && <Loader2 className="h-4 w-4 animate-spin" style={{ color: T }} />}
                {selectedCreators.size > 0 && (
                  <Link to="/build-wave" className="text-[10px] px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5 no-underline" style={{ background: `${T}15`, color: T, border: `1px solid ${T}30` }} data-testid="push-to-wave-btn">
                    <ShoppingCart className="h-3 w-3" />{selectedCreators.size} selected — Use these in a wave <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-3 w-3 text-gray-600" />
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="px-2.5 py-1.5 rounded-lg text-[10px] text-white focus:outline-none" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="sort-select">
                  <option value="engagement">Engagement Rate · highest first</option>
                  <option value="followers">Followers · micro first ↑</option>
                  <option value="followers_desc">Followers · macro first ↓</option>
                  <option value="rating">Rating</option>
                </select>
              </div>
            </div>

            {/* Wave Composition (formerly "CMD Cohort Map") — safe-surface
                only. Empty/degraded states render in plain English; the
                "Build wave from this view" CTA is the one-click action.
                See /app/docs/DESIGN_CONTRACT.md for the framing. */}
            <CmdCohortMap
              signals={cmdSignals}
              creators={displayed}
              brandScale={brandScale}
              onAddAllToWave={addAllToWave}
            />

            {/* Creator Grid */}
            <div className={`grid gap-3 ${showBuilder ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`} data-testid="creator-grid">
              {displayed.map(creator => (
                <CreatorCard
                  key={creator.id}
                  creator={creator}
                  selected={selectedCreators.has(creator.id)}
                  onToggle={toggleCreator}
                  cmdTag={cmdSignals.tags[creator.id]}
                />
              ))}
            </div>

            {displayed.length === 0 && !loading && (
              <div className="text-center py-16">
                <Users className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No creators match your filters</p>
                <button onClick={() => setFilters({})} className="text-xs mt-2 hover:underline" style={{ color: T }}>Clear all filters</button>
              </div>
            )}
          </div>
        </div>

        <CreatorProfileModal
          handle={profileHandle}
          open={!!profileHandle}
          onOpenChange={(o) => !o && setProfileHandle(null)}
        />

        <MatchExplainerModal
          open={!!explainCreator}
          creator={explainCreator}
          onOpenChange={(o) => !o && setExplainCreator(null)}
          onViewFullProfile={(h) => { setExplainCreator(null); setProfileHandle(h); }}
        />
      </div>
    </DashboardLayout>
  );
}
